import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IMAGE_BASE_URL } from "../../config/constants";
import {
  fetchUserAccounts,
  updateUserStatus,
  createUserAccount,
  updateUserCredentials,
} from "../../redux/admin/userAccounts.thunk";
import { fetchCourseCategories } from "../../redux/admin/courseCategory.thunk";
import { setUserCurrentPage } from "../../redux/admin/userAccounts.slice";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Role Config ────────────────────────────────────────────────────────── */
const ROLES = {
  6:  { label: "Instructor",  color: "#1e40af", bg: "#dbeafe" },
  1:  { label: "Super Admin", color: "#991b1b", bg: "#fee2e2" },
};
const INSTRUCTOR_ROLE_ID = "6";

const roleStyle = (id) =>
  ROLES[id] ?? { label: "Member", color: "#3c3c3c", bg: "#f5f5f7" };

/* ─── Avatar Helpers ─────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  ["#1e40af", "#dbeafe"], ["#6b21a8", "#f3e8ff"], ["#065f46", "#d1fae5"],
  ["#92400e", "#fef3c7"], ["#881337", "#ffe4e6"], ["#0f766e", "#ccfbf1"],
];
const avatarColor = (name = "") => {
  const i = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[i];
};

/* ─── Spinner & Skeleton Component ───────────────────────────────────────── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "ua-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "ua-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

/* ─── Initial Form State ──────────────────────────────────────────────────── */
const EMPTY_FORM = {
  id: null, user_id: null,
  username: "", name: "", last_name: "",
  email: "", password: "",
  mobile_no: "", dob: "", gender: "M",
  city: "", role_id: INSTRUCTOR_ROLE_ID, is_active: 1,
  course_ids: [],
};

export default function UserAccounts() {
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const { users = [], totalPages = 1, currentPage = 1, total = 0, loading = false } =
    useSelector((s) => s.admin?.userAccounts ?? {});

  const courseCategoryState = useSelector((state) => state.admin?.courseCategory || {});
  const globalCategories = courseCategoryState.courseCategories || courseCategoryState.categories || [];
  
  const courseOptions = globalCategories.map((c) => ({
    id: c.id,
    name: c.name || c.title || c.category_name || `Course ${c.id}`,
  }));

  const courseName = (id) => courseOptions.find((c) => String(c.id) === String(id))?.name ?? id;

  /* =====================================================
     LOCAL UI STATES
  ====================================================== */
  const [search, setSearch] = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); // profile | security | workspace
  const [saving, setSaving] = useState(false);
  
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPrev, setAvatarPrev] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [pwVisible, setPwVisible] = useState(false);

  const isInstructor = form.role_id === INSTRUCTOR_ROLE_ID;

  /* =====================================================
     LIFECYCLE EVENTS
  ====================================================== */
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search.trim()), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    dispatch(fetchUserAccounts({ page: currentPage, limit, search: debSearch }));
  }, [dispatch, currentPage, debSearch]);

  useEffect(() => {
    dispatch(fetchCourseCategories());
  }, [dispatch]);

  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) dispatch(setUserCurrentPage(p));
  };

  /* =====================================================
     HANDLERS & DOMAIN OPERATIONS
  ====================================================== */
  const toggleStatus = async (id, cur) => {
    try {
      await dispatch(updateUserStatus({ id, isActive: cur === 0 })).unwrap();
      toastr.success("Account status updated");
    } catch (e) { 
      toastr.error(e || "Failed to update status"); 
    }
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { 
      toastr.error("Image dimensions exceed 5 MB budget limit."); 
      return; 
    }
    setAvatarFile(f);
    setAvatarPrev(URL.createObjectURL(f));
  };

  const openAddModal = () => {
    setIsEditMode(false); setAvatarFile(null); setAvatarPrev("");
    setForm(EMPTY_FORM); setActiveTab("profile"); setPwVisible(false);
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setIsEditMode(true); setAvatarFile(null);
    setAvatarPrev(u.image ? `${IMAGE_BASE_URL}/${u.image}` : "");
    setForm({
      id: u.id, user_id: u.user_id,
      username: u.username ?? "", name: u.name ?? "",
      last_name: u.last_name ?? "", email: u.email ?? "",
      password: "", mobile_no: u.mobile_no ?? "",
      dob: u.dob ?? "", gender: u.gender ?? "M",
      city: u.city ?? "", role_id: String(u.role_id ?? INSTRUCTOR_ROLE_ID),
      is_active: u.is_active,
      course_ids: (u.course_ids ?? u.courses ?? []).map((c) => (typeof c === "object" ? c.id : c)),
    });
    setActiveTab("profile"); setPwVisible(false);
    setShowModal(true);
  };

  const toggleCourse = (id) => {
    setForm((f) => {
      const has = f.course_ids.includes(id);
      if (has) return { ...f, course_ids: f.course_ids.filter((x) => x !== id) };
      return { ...f, course_ids: [...f.course_ids, id] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.mobile_no)) {
      toastr.error("Mobile allocation must run exactly 10 digits"); return;
    }
    if (isInstructor && form.course_ids.length < 1) {
      toastr.error("Allocation parameter requires at least 1 assigned course workspace"); 
      setActiveTab("workspace");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    ["username","name","last_name","email","mobile_no","dob","gender","city","role_id"]
      .forEach((k) => fd.append(k, form[k] || (k === "username" ? form.mobile_no : "")));
    fd.append("course_ids", JSON.stringify(isInstructor ? form.course_ids : []));
    if (avatarFile) { fd.append("image", avatarFile); fd.append("is_image_replaced","1"); }

    try {
      if (isEditMode) {
        if (form.password && form.password.length < 8) {
          toastr.error("Security standard: Password must be at least 8 characters"); setSaving(false); return;
        }
        if (form.password) fd.append("password", form.password);
        await dispatch(updateUserCredentials({ userId: form.user_id, credentialData: fd })).unwrap();
        toastr.success("Account profile configured");
      } else {
        if (!form.password) { toastr.error("Security assignment required: Password is mandatory"); setSaving(false); return; }
        fd.append("password", form.password); fd.append("is_active", form.is_active);
        await dispatch(createUserAccount(fd)).unwrap();
        toastr.success("User credentials deployed");
      }
      setShowModal(false);
      dispatch(fetchUserAccounts({ page: currentPage, limit, search: debSearch }));
    } catch (err) { 
      toastr.error(err || "Operation terminated unexpectedly");
    } finally { 
      setSaving(false); 
    }
  };

  const renderPagination = () => {
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);
    
    return nums.map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => handlePageChange(n)}
        className={`ua-page-btn${currentPage === n ? " active" : ""}`}
      >
        {n}
      </button>
    ));
  };

  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCourseCount = form.course_ids.length;

  return (
    <div className="ua-page">
      <div className="ua-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="ua-header">
          <div>
            <p className="ua-eyebrow">User Accounts Management Center</p>
            <h1 className="ua-title">Users Directory</h1>
          </div>
          <button type="button" className="ua-btn-primary" onClick={openAddModal}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"/>
            </svg> 
            Add User Account
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="ua-kpi-grid">
          {[
            { label: "Total Accounts Registered", value: total },
            { label: "View Segment Count", value: users.length },
            { label: "Console Index Location", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="ua-kpi">
              <span className="ua-kpi-label">{label}</span>
              <span className="ua-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="ua-toolbar">
          <div className="ua-search">
            <svg className="ua-search-icon" width="14" height="14" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="#aeaeb2" strokeWidth="1.8"/>
              <path d="M13.5 13.5L17 17" stroke="#aeaeb2" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Query matrix identity (Name, Email or City)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── DATA CORE TABLE ── */}
        <div className="ua-card">
          <div className="table-responsive">
            <table className="ua-table">
              <thead>
                <tr>
                  <th style={{ width: "26%" }}>User</th>
                  <th style={{ width: "20%" }}>Contact Infrastructure</th>
                  <th style={{ width: "14%" }}>Identity Classification</th>
                  <th style={{ width: "16%" }}>Course Allocation Matrix</th>
                  <th style={{ width: "14%" }}>Geographic Demographics</th>
                  <th style={{ width: "10%" }}>Status Grid</th>
                  <th className="text-end" style={{ width: "6%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="ua-row">
                      <td>
                        <div className="ua-name-cell">
                          <Skeleton w={34} h={34} r={50} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="75%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <Skeleton w="80%" h={11} />
                          <Skeleton w="50%" h={10} />
                        </div>
                      </td>
                      <td><Skeleton w={70} h={20} r={6} /></td>
                      <td><Skeleton w={90} h={18} r={6} /></td>
                      <td><Skeleton w="60%" h={12} /></td>
                      <td><Skeleton w={54} h={20} r={20} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={32} h={32} r={8} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="ua-empty-state">
                      <div className="ua-empty-icon-wrap">✕</div>
                      <p className="ua-empty-title">No user records detected</p>
                      <span className="ua-empty-desc">Refine execution query parameters or build new credentials profile.</span>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const uCourseIds = (u.course_ids ?? u.courses ?? []).map((c) => (typeof c === "object" ? c.id : c));
                    const badge = roleStyle(String(u.role_id));
                    return (
                      <tr key={u.id} className="ua-row">
                        <td>
                          <div className="ua-name-cell">
                            <AvatarDisplay user={u} />
                            <div>
                              <div className="ua-name">{u.name} {u.last_name}</div>
                              <div className="ua-loc">@{u.username || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="ua-contact-main">{u.email}</div>
                          <div className="ua-contact-sub">{u.mobile_no}</div>
                        </td>
                        <td>
                          <span className="ua-role-badge" style={{ background: badge.bg, color: badge.color }}>
                            {badge.label}
                          </span>
                        </td>
                        <td>
                          {String(u.role_id) === INSTRUCTOR_ROLE_ID && uCourseIds.length > 0 ? (
                            <div className="ua-course-tag-wrap">
                              {uCourseIds.map((cid) => (
                                <span key={cid} className="ua-course-tag">{courseName(cid)}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="ua-empty-dash">—</span>
                          )}
                        </td>
                        <td>
                          <div className="ua-contact-main">{u.city || "—"}</div>
                          <div className="ua-contact-sub">{u.gender === "F" ? "Female" : "Male"}</div>
                        </td>
                        <td>
                          <StatusToggleInput active={u.is_active === 1} onChange={() => toggleStatus(u.id, u.is_active)} />
                        </td>
                        <td className="text-end">
                          <button type="button" className="ua-icon-btn" onClick={() => openEditModal(u)} title="Modify User Records">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── TABLE FOOTER PANEL ── */}
          <div className="ua-footer">
            <span className="ua-count">{users.length} of {total} operational indices</span>
            <div className="ua-pagination">
              <button type="button" className="ua-page-btn ua-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                ‹
              </button>
              {renderPagination()}
              <button type="button" className="ua-page-btn ua-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL INTERACTIVE WORKSPACE
      ═══════════════════════════════════════ */}
      {showModal && (
        <div className="ua-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div className="ua-modal">

            {/* Modal Left Sidebar Selector Workspace */}
            <div className="ua-modal-side">
              <div className="ua-modal-side-header">
                <p className="ua-modal-side-title">Account Profiles</p>
                <p className="ua-modal-side-sub">Control Parameters Panel</p>
              </div>
              <nav className="ua-modal-nav">
                <button
                  type="button"
                  className={`ua-nav-item${activeTab === "profile" ? " active" : ""}`}
                  onClick={() => setActiveTab("profile")}
                  disabled={saving}
                >
                  <span className="ua-nav-icon">👤</span>
                  <span>Personal Data</span>
                </button>
                <button
                  type="button"
                  className={`ua-nav-item${activeTab === "security" ? " active" : ""}`}
                  onClick={() => setActiveTab("security")}
                  disabled={saving}
                >
                  <span className="ua-nav-icon">🔑</span>
                  <span>Credentials Protocol</span>
                </button>
                {isInstructor && (
                  <button
                    type="button"
                    className={`ua-nav-item${activeTab === "workspace" ? " active" : ""}`}
                    onClick={() => setActiveTab("workspace")}
                    disabled={saving}
                  >
                    <span className="ua-nav-icon">📚</span>
                    <span>Course Workspaces</span>
                    {selectedCourseCount > 0 && <span className="ua-nav-badge">{selectedCourseCount}</span>}
                  </button>
                )}
              </nav>
            </div>

            {/* Modal Form Content Workspace */}
            <form onSubmit={handleSubmit} className="ua-modal-main">
              <div className="ua-modal-header">
                <div>
                  <h2 className="ua-modal-title">{isEditMode ? "Modify Account Profile" : "Register New Account"}</h2>
                  <p className="ua-modal-sub">
                    {activeTab === "profile" && "Manage demographics and basic personal metadata layers."}
                    {activeTab === "security" && "Configure authentication parameters and classification roles."}
                    {activeTab === "workspace" && "Map and coordinate academic module boundaries."}
                  </p>
                </div>
                <button type="button" className="ua-close-x" onClick={() => setShowModal(false)} disabled={saving}>✕</button>
              </div>

              <div className="ua-modal-body">
                
                {/* ── SECTION 1: PERSONAL CREDENTIALS / DEMOGRAPHICS DATA ── */}
                {activeTab === "profile" && (
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="ua-avatar-section">
                        <div className="ua-avatar-circle" onClick={() => !saving && fileRef.current.click()}>
                          {avatarPrev ? (
                            <img src={avatarPrev} alt="Identity Preview" />
                          ) : (
                            <span className="ua-avatar-placeholder-text">Photo Link</span>
                          )}
                        </div>
                        <div>
                          <button type="button" className="ua-btn-outline font-12" onClick={() => fileRef.current.click()} disabled={saving}>
                            Upload Image File
                          </button>
                          <p className="ua-upload-hint">JPG, PNG or WEBP structure · Threshold limit 5 MB</p>
                        </div>
                        <input type="file" ref={fileRef} accept="image/*" style={{ display: "none" }} onChange={handleFile} />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="ua-label">First Name *</label>
                      <input type="text" className="ua-field" placeholder="e.g. Anandaraj" value={form.name} onChange={e => field("name", e.target.value)} required disabled={saving} />
                    </div>
                    <div className="col-md-6">
                      <label className="ua-label">Last Name *</label>
                      <input type="text" className="ua-field" placeholder="e.g. L" value={form.last_name} onChange={e => field("last_name", e.target.value)} required disabled={saving} />
                    </div>

                    <div className="col-md-6">
                      <label className="ua-label">Date of Birth Mapping</label>
                      <input type="date" className="ua-field" value={form.dob} onChange={e => field("dob", e.target.value)} disabled={saving} />
                    </div>
                    <div className="col-md-6">
                      <label className="ua-label">Gender Demographics Classification</label>
                      <select className="ua-field" value={form.gender} onChange={e => field("gender", e.target.value)} disabled={saving}>
                        <option value="M">Male Node</option>
                        <option value="F">Female Node</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="ua-label">Geographic City Location</label>
                      <input type="text" className="ua-field" placeholder="e.g. Chennai" value={form.city} onChange={e => field("city", e.target.value)} disabled={saving} />
                    </div>
                  </div>
                )}

                {/* ── SECTION 2: ROLES & CREDENTIALS INFRASTRUCTURE ── */}
                {activeTab === "security" && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="ua-label">Username Descriptor Identifier</label>
                      <input type="text" className="ua-field" placeholder="Default mapping via phone node" value={form.username} onChange={e => field("username", e.target.value)} disabled={saving} />
                    </div>
                    <div className="col-md-6">
                      <label className="ua-label">Mobile Network Node *</label>
                      <input type="text" className="ua-field" placeholder="10-digit phone index" value={form.mobile_no} onChange={e => field("mobile_no", e.target.value)} required disabled={saving} />
                    </div>

                    <div className="col-12">
                      <label className="ua-label">Email Base Terminal Infrastructure *</label>
                      <input type="email" className="ua-field" placeholder="identity@terminal.com" value={form.email} onChange={e => field("email", e.target.value)} required disabled={saving} />
                    </div>

                    <div className="col-12">
                      <label className="ua-label">Identity Classification Role Nodes</label>
                      <select
                        className="ua-field"
                        value={form.role_id}
                        onChange={e => {
                          const val = e.target.value;
                          setForm((f) => ({ ...f, role_id: val, course_ids: val === INSTRUCTOR_ROLE_ID ? f.course_ids : [] }));
                        }}
                        disabled={saving}
                      >
                        <option value="6">Instructor Assignment</option>
                        <option value="1">Super Admin Authority</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="ua-label">
                        {isEditMode ? "Update Authentication Matrix (Leave empty to reserve status quo)" : "Authentication Protocol Password *"}
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type={pwVisible ? "text" : "password"}
                          className="ua-field"
                          style={{ paddingRight: "50px" }}
                          placeholder="Minimum 8 index keys standard"
                          value={form.password}
                          onChange={e => field("password", e.target.value)}
                          required={!isEditMode}
                          disabled={saving}
                        />
                        <button type="button" className="ua-pwd-toggle-btn" onClick={() => setPwVisible(v => !v)}>
                          {pwVisible ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SECTION 3: ASSIGNED COURSE SYNC WORKSPACE ── */}
                {activeTab === "workspace" && isInstructor && (
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="ua-section-note">
                        Allocate and map academic course configurations for this instructor profile. There is no restriction on the maximum count.
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <label className="ua-label">
                        Course Matrix Cluster Execution ({selectedCourseCount} Selected)
                      </label>
                      
                      <div className="ua-courses-picker-frame">
                        <CourseDropdownSelector 
                          options={courseOptions}
                          selected={form.course_ids}
                          onToggle={toggleCourse}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                )}

              </div>

              <div className="ua-modal-footer">
                <button type="button" className="ua-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ua-btn-primary ua-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      <span>Syncing Registry Node...</span>
                    </>
                  ) : (
                    <span>{isEditMode ? "Save Parameter Changes" : "Deploy Credentials Profile"}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CORE STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes ua-spin { to { transform: rotate(360deg); } }
        @keyframes ua-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes ua-modal-in {
          from { opacity: 0; transform: scale(0.98) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
        @keyframes ua-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .ua-page { background: #f5f5f7; min-height: 100vh; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; color: #1d1d1f; font-size: 14px; }
        .ua-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }
        .ua-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .ua-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .ua-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        .ua-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        .ua-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .ua-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .ua-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        .ua-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .ua-search { flex: 1; min-width: 200px; position: relative; }
        .ua-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; }
        .ua-search input { width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px; padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit; }
        .ua-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .ua-search input::placeholder { color: #aeaeb2; }

        .ua-btn-primary { background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: background 0.15s; white-space: nowrap; font-family: inherit; justify-content: center; }
        .ua-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .ua-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .ua-btn-submit { padding: 9px 22px; min-width: 170px; }
        .ua-btn-outline { border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit; }
        .ua-btn-outline:hover:not(:disabled) { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }
        .ua-btn-cancel { border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; font-family: inherit; }
        .ua-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }

        .ua-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .ua-table { width: 100%; border-collapse: collapse; }
        .ua-table thead th { background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px; border-bottom: 1px solid #e8e8ed; white-space: nowrap; }
        .ua-table td { padding: 13px 18px; vertical-align: middle; }
        .ua-row:hover { background: #fafafa; }

        .ua-name-cell { display: flex; align-items: center; gap: 10px; }
        .ua-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .ua-loc { font-size: 11.5px; color: #aeaeb2; margin-top: 1px; }
        .ua-contact-main { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .ua-contact-sub { font-size: 12px; color: #8e8e93; margin-top: 1px; }

        .ua-role-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11px; font-weight: 600; white-space: nowrap; }
        .ua-course-tag-wrap { display: flex; flex-wrap: wrap; gap: 4px; max-width: 240px; }
        .ua-course-tag { background: #f5f3ff; color: #5b21b6; border: 1px solid #ede9fe; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
        .ua-empty-dash { color: #d2d2d7; }
        .ua-icon-btn { border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: #6e6e73; transition: all 0.12s; }
        .ua-icon-btn:hover { border-color: #aeaeb2; color: #1d1d1f; background: #f5f5f7; }

        .ua-empty-state { text-align: center; padding: 3rem 1rem; }
        .ua-empty-icon-wrap { width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.2rem; color: #aeaeb2; margin-bottom: 12px; }
        .ua-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .ua-empty-desc { font-size: 12px; color: #aeaeb2; }

        .ua-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; }
        .ua-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .ua-pagination { display: flex; gap: 4px; }
        .ua-page-btn { min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff; border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; font-family: inherit; }
        .ua-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .ua-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── MODAL COMPACT LAYOUT (DESKTOP FIT NO SCROLL) ── */
        .ua-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: ua-overlay-in 0.2s ease; }
        .ua-modal { 
          width: 100%; max-width: 860px; height: 510px; background: #fff; border-radius: 16px; border: 1px solid #d2d2d7; 
          box-shadow: 0 32px 64px -12px rgba(0,0,0,0.16); display: flex; overflow: hidden; animation: ua-modal-in 0.22s cubic-bezier(0.25, 1, 0.5, 1); 
        }
        
        .ua-modal-side { width: 200px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .ua-modal-side-header { padding: 16px; border-bottom: 1px solid #e8e8ed; }
        .ua-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .ua-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 2px 0 0; }
        .ua-modal-nav { padding: 6px; display: flex; flex-direction: column; gap: 2px; }
        
        .ua-nav-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px; border: none; background: transparent; font-size: 13px; color: #6e6e73; cursor: pointer; text-align: left; width: 100%; transition: all 0.1s; position: relative; font-family: inherit; }
        .ua-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .ua-nav-badge { background: #0071e3; color: #fff; font-size: 10px; font-weight: 600; border-radius: 20px; padding: 1px 6px; position: absolute; right: 10px; top: 50%; transform: translateY(-50%); }

        .ua-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; background: #fff; }
        .ua-modal-header { padding: 16px 24px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; flex-shrink: 0; }
        .ua-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .ua-modal-sub { font-size: 12px; color: #aeaeb2; margin: 2px 0 0; }
        .ua-close-x { background: transparent; border: none; font-size: 15px; color: #aeaeb2; cursor: pointer; padding: 4px; line-height: 1; }
        
        /* Fixed workspace viewport container budget fit */
        .ua-modal-body { flex: 1; overflow-y: visible; padding: 20px 24px; display: flex; flex-direction: column; justify-content: flex-start; }
        .ua-modal-footer { padding: 12px 24px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; flex-shrink: 0; }

        .ua-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
        .ua-field { width: 100%; border: 1px solid #d2d2d7; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #1d1d1f; background: #fff; outline: none; transition: border-color 0.12s; font-family: inherit; }
        .ua-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.12); }
        select.ua-field { cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%238e8e93' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; background-size: 14px; padding-right: 32px; }

        .ua-pwd-toggle-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: transparent; border: none; font-size: 11px; font-weight: 600; color: #0071e3; cursor: pointer; }
        .ua-section-note { background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 8px; padding: 10px 14px; font-size: 12.5px; color: #6e6e73; }

        .ua-avatar-section { display: flex; align-items: center; gap: 12px; background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 8px; padding: 10px; margin-bottom: 4px; width: 100%; }
        .ua-avatar-circle { width: 46px; height: 46px; border-radius: 50%; border: 1.5px dashed #d2d2d7; background: #fff; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .ua-avatar-circle img { width: 100%; height: 100%; object-fit: cover; }
        .ua-avatar-placeholder-text { font-size: 9px; color: #aeaeb2; font-weight: 500; }
        .ua-upload-hint { font-size: 11px; color: #aeaeb2; margin: 2px 0 0 0; }

        .ua-toggle-box { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
        .ua-toggle-track { position: relative; width: 34px; height: 18px; background: #d2d2d7; border-radius: 10px; }
        .ua-toggle-thumb { position: absolute; width: 14px; height: 14px; border-radius: 50%; background: #fff; top: 2px; left: 2px; transition: left 0.15s; }
        .ua-toggle-box.active .ua-toggle-track { background: #34c759; }
        .ua-toggle-box.active .ua-toggle-thumb { left: 18px; }
        .ua-toggle-txt { font-size: 12px; font-weight: 500; color: #6e6e73; }

        .ua-courses-picker-frame { width: 100%; position: relative; margin-top: 2px; }
        .ua-ms-ctrl { display: flex; align-items: center; justify-content: space-between; min-height: 36px; border: 1px solid #d2d2d7; border-radius: 8px; padding: 4px 10px; background: #fff; cursor: pointer; flex-wrap: wrap; gap: 6px; }
        .ua-ms-chips { display: flex; flex-wrap: wrap; gap: 4px; flex: 1; }
        .ua-ms-chip { display: inline-flex; align-items: center; gap: 5px; background: #f5f3ff; color: #5b21b6; border: 1px solid #ede9fe; font-size: 11.5px; font-weight: 600; padding: 1px 6px 1px 8px; border-radius: 5px; }
        .ua-ms-x { background: transparent; border: none; color: #5b21b6; font-size: 13px; cursor: pointer; padding: 0; }
        .ua-ms-placeholder { color: #aeaeb2; font-size: 13px; }
        .ua-ms-arrow { font-size: 9px; color: #8e8e93; }
        
        .ua-ms-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: #fff; border: 1px solid #d2d2d7; border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.06); max-height: 140px; overflow-y: auto; z-index: 100; }
        .ua-ms-opt { display: flex; align-items: center; padding: 8px 12px; font-size: 13px; color: #1d1d1f; cursor: pointer; border-bottom: 1px solid #f5f5f7; }
        .ua-ms-opt input { margin-right: 8px; accent-color: #0071e3; width: 14px; height: 14px; }
        .ua-ms-empty-info { padding: 10px; font-size: 12px; color: #aeaeb2; text-align: center; }
        .ua-ms-hint-caption { font-size: 11px; color: #aeaeb2; margin: 4px 2px 0; }

        .row { display: flex; flex-wrap: wrap; margin-right: -8px; margin-left: -8px; }
        .col-12 { flex: 0 0 100%; max-width: 100%; padding-right: 8px; padding-left: 8px; }
        .col-md-6 { flex: 0 0 50%; max-width: 50%; padding-right: 8px; padding-left: 8px; }
        .text-end { text-align: right; }
        .table-responsive { overflow-x: auto; width: 100%; }

        /* Mobile Breakpoint Fallback Adjustment Stack Configuration */
        @media (max-width: 768px) {
          .col-md-6 { flex: 0 0 100%; max-width: 100%; }
          .ua-modal { flex-direction: column; height: auto; max-height: 92vh; }
          .ua-modal-side { width: 100%; border-bottom: 1px solid #e8e8ed; border-right: none; }
          .ua-modal-side-header { display: none; }
          .ua-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; }
          .ua-nav-item { white-space: nowrap; width: auto; font-size: 12px; padding: 6px 12px; }
          .ua-nav-badge { position: relative; right: auto; top: auto; transform: none; margin-left: 4px; }
          .ua-modal-body { overflow-y: auto; padding: 16px; max-height: 380px; }
        }
      `}</style>
    </div>
  );
}

/* ─── COMPACT SUB-COMPONENTS LAYER ────────────────────────────────────────── */

function AvatarDisplay({ user, size = 34 }) {
  const [imgErr, setImgErr] = useState(false);
  const [fg, bg] = avatarColor(user.name);
  const initials = [user.name?.[0], user.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "U";
  
  if (user.image && !imgErr) {
    return (
      <img
        src={`${IMAGE_BASE_URL}/${user.image}`}
        alt={user.name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid #e8e8ed" }}
        onError={() => setImgErr(true)}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 600, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function StatusToggleInput({ active, onChange }) {
  return (
    <div className={`ua-toggle-box${active ? " active" : ""}`} onClick={onChange}>
      <div className="ua-toggle-track">
        <div className="ua-toggle-thumb" />
      </div>
      <span className="ua-toggle-txt">{active ? "Active" : "Inactive"}</span>
    </div>
  );
}

function CourseDropdownSelector({ options, selected, onToggle, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const clickHandler = (e) => { 
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); 
    };
    document.addEventListener("mousedown", clickHandler);
    return () => document.removeEventListener("mousedown", clickHandler);
  }, []);

  const selectedItems = options.filter((o) => selected.includes(o.id));

  return (
    <div ref={dropdownRef} style={{ width: "100%", position: "relative" }}>
      <div className="ua-ms-ctrl" onClick={() => !disabled && setIsOpen(v => !v)}>
        {selectedItems.length === 0 ? (
          <span className="ua-ms-placeholder">Select active courses from the module cluster...</span>
        ) : (
          <div className="ua-ms-chips">
            {selectedItems.map((c) => (
              <span key={c.id} className="ua-ms-chip">
                {c.name}
                <button
                  type="button"
                  className="ua-ms-x"
                  disabled={disabled}
                  onClick={(e) => { e.stopPropagation(); if(!disabled) onToggle(c.id); }}
                >×</button>
              </span>
            ))}
          </div>
        )}
        <span className={`ua-ms-arrow${isOpen ? " open" : ""}`}>▼</span>
      </div>
      
      {isOpen && (
        <div className="ua-ms-dropdown">
          {options.length === 0 ? (
            <div className="ua-ms-empty-info">No academic categories found inside database infrastructure node.</div>
          ) : (
            options.map((c) => {
              const isChecked = selected.includes(c.id);
              return (
                <label key={c.id} className="ua-ms-opt">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => !disabled && onToggle(c.id)}
                  />
                  <span>{c.name}</span>
                </label>
              );
            })
          )}
        </div>
      )}
      <p className="ua-ms-hint-caption">{selected.length} courses configured</p>
    </div>
  );
}